import { Alert } from '@creditornot/cb-alert';
import { Avatar } from '@creditornot/cb-avatar';
import { Button } from '@creditornot/cb-button';
import { cssThemedValue, Typography as T } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import { localize } from 'common/utils';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from 'modules/shared-menu/types';

import { ModalHeaderWithSubheader } from './ModalHeaderWithSubheader';
import {
  ImageContainer,
  NameAndAddress,
  NoteContainer,
  StyledModalFooter,
  VenueItemLeftContent,
  VenueItemRightContent,
  VenueItemRoot,
  VenueList,
} from './styles';

type Props = {
  error: any;
  onClose: () => void;
  onGoBack: () => void;
  onSubmit: () => void;
  selectedVenues: VenueWithSharedMenuLinkInfo[];
  sharedMenu: SharedMenu;
  submitting: boolean;
  isMenuEmpty: boolean;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  font-weight: bold;
`;

const VenueItem = styled(VenueItemRoot)``;

const FooterButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

export const ConfirmAndShare = ({
  error,
  submitting,
  onGoBack,
  onClose,
  onSubmit,
  selectedVenues,
  sharedMenu,
  isMenuEmpty,
}: Props) => {
  const intl = useIntl();

  const venuesAlreadyUsingSharedMenus = useMemo(() => {
    return selectedVenues.filter(venue => venue.sharedMenuLink.scope !== 'none');
  }, [selectedVenues]);

  return (
    <Root>
      <ModalHeaderWithSubheader
        onClose={onClose}
        header={<FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.title" />}
        subheader={
          <FormattedMessage
            id="shared-menu.link.shared-menu-selected.confirm-view.subheader"
            values={{
              name: localize(sharedMenu.name, intl.locale),
              count: selectedVenues.length,
            }}
          />
        }
      />
      {(isMenuEmpty || !!venuesAlreadyUsingSharedMenus.length) && (
        <NoteContainer>
          {isMenuEmpty && (
            <Alert
              key="menu-empty"
              title={intl.formatMessage({ id: 'shared-menu.link.venues-selected.menu-empty-note-label' })}
              size="medium"
              variant="warning"
            >
              <FormattedMessage id="shared-menu.link.venues-selected.menu-empty-note" />
            </Alert>
          )}
          {!!venuesAlreadyUsingSharedMenus.length && (
            <Alert key="venues-already-linked" variant="warning" size="medium">
              <FormattedMessage
                id="listing-manager.selections.venue-management.share.confirm-view.venues-already-linked-warning"
                values={{
                  name: localize(sharedMenu.name, intl.locale),
                  count: venuesAlreadyUsingSharedMenus.length,
                }}
              />
            </Alert>
          )}
        </NoteContainer>
      )}
      <ListHeader>
        <T.Body3>
          <FormattedMessage id="shared-menu.navigation.venues.title" />
        </T.Body3>
        <T.Body3>
          <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.current-menu" />
        </T.Body3>
      </ListHeader>
      <VenueList>
        {selectedVenues.map(venue => (
          <VenueItem key={venue.id}>
            <VenueItemLeftContent>
              <ImageContainer>
                <Avatar size="tiny" imgSrc={venue.listimage ?? undefined} />
              </ImageContainer>
              <NameAndAddress>
                <T.Body3>{localize(venue.name, intl.locale)}</T.Body3>
                <T.Small>{venue.address}</T.Small>
              </NameAndAddress>
            </VenueItemLeftContent>
            <VenueItemRightContent>
              {venue.sharedMenuLink.scope === 'none' && '-'}
              {venue.sharedMenuLink.scope === sharedMenu.scope.type && (
                <Tag size="small" variant="lightYellow">
                  {venue.sharedMenuLink.sharedMenuName}
                </Tag>
              )}
              {venue.sharedMenuLink.scope !== sharedMenu.scope.type && venue.sharedMenuLink.scope !== 'none' && (
                <Tag size="small" variant="lightYellow">
                  <FormattedMessage id="shared-menu.venue-list.other-scope" />
                </Tag>
              )}
            </VenueItemRightContent>
          </VenueItem>
        ))}
      </VenueList>
      <StyledModalFooter style={{ flexDirection: 'column' }}>
        {error && <APIErrorNotification error={'error'} style={{ marginBlockEnd: '16px', width: '100%' }} />}

        <FooterButtons>
          <Button
            variant="lightBlue"
            size="small"
            onClick={onGoBack}
            style={{ marginInlineEnd: '8px' }}
            disabled={submitting}
          >
            <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.back-button-label" />
          </Button>
          <Button
            variant="blue"
            size="small"
            loading={submitting}
            onClick={() => {
              onSubmit();
            }}
          >
            <FormattedMessage id="shared-menu.link.shared-menu-selected.confirm-view.submit-button-label" />
          </Button>
        </FooterButtons>
      </StyledModalFooter>
    </Root>
  );
};
